/**
 * MACRO #18 — Content Interactivity: shared pointer-arbitration primitive for
 * EVERY interactive published Canvas anchor (LINK / SOCIAL per-destination /
 * CONTACTS per-entry / PDF). It integrates with the Grid's canonical gesture
 * machine (`TAP_SLOP_PX`) so that:
 *
 *   • a small tap on an actionable element activates the action EXACTLY once
 *     (no Grid selection, no pan);
 *   • a real drag STARTING on an actionable element is treated as viewer
 *     navigation — the Grid PANS and the action does NOT fire on release.
 *
 * There is no new threshold, no timer and no timing hack: tap-vs-drag is decided
 * by the exact same movement-distance state the Grid canvas already uses.
 *
 * When no arbiter is present in context (standalone `#p/<id>` public page or the
 * private editor), the anchor degrades to a plain, safe native anchor (native
 * click / keyboard Enter, no Grid pan) — identical to the prior behaviour.
 */
import { createContext, useContext, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent, type DragEvent as ReactDragEvent } from "react";

export interface ContentPointerArbiter {
  /** Arm the Grid gesture from a pointer-down that landed on an anchor. */
  down(e: ReactPointerEvent): void;
  /** Feed movement — pans the Grid once the canonical slop is exceeded. */
  move(e: ReactPointerEvent): void;
  /** Resolve the gesture. Returns true if it was a TAP (activate the action),
   *  false if it became a Grid drag (suppress activation — Grid already panned). */
  up(e: ReactPointerEvent): boolean;
  /** Pointer capture lost mid-gesture → resolve safely without activation. */
  cancel(e: ReactPointerEvent): void;
}

export const ContentPointerContext = createContext<ContentPointerArbiter | null>(null);

export interface ContentAnchorProps {
  className: string;
  draggable: boolean;
  onPointerDown: (e: ReactPointerEvent<HTMLAnchorElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLAnchorElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLAnchorElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLAnchorElement>) => void;
  onMouseDown: (e: ReactMouseEvent<HTMLAnchorElement>) => void;
  onClick: (e: ReactMouseEvent<HTMLAnchorElement>) => void;
  onDragStart: (e: ReactDragEvent<HTMLAnchorElement>) => void;
}

const noop = () => {};
const ANCHOR_CLASS = "grid-pub-anchor";
// An <a href> is natively draggable: a real pointer DRAG that STARTS on an
// anchor would otherwise trigger the browser's link drag-and-drop, which fires
// `pointercancel` after ~2 moves — killing the Grid pan mid-gesture (the action
// is still correctly suppressed, but the Grid barely moves). Neutralising the
// native drag keeps the full pointer stream flowing into the gesture machine so
// a drag pans the Grid across its ENTIRE travel.
const stopNativeDrag = (e: ReactDragEvent<HTMLAnchorElement>) => e.preventDefault();

/**
 * Block exactly the NEXT native click on this element (the one the browser may
 * synthesize after a short drag) and then remove the guard on the next frame so
 * it can never linger and swallow a later keyboard-Enter activation. Deterministic
 * and per-element — no persistent flag.
 */
function blockNextClick(el: HTMLElement): void {
  const block = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
  };
  el.addEventListener("click", block, { capture: true, once: true } as AddEventListenerOptions);
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => el.removeEventListener("click", block, true));
  }
}

export function useContentAnchorProps(): ContentAnchorProps {
  const arbiter = useContext(ContentPointerContext);
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  if (!arbiter) {
    // Fallback (no Grid to pan): preserve the prior safe-anchor behaviour.
    return {
      className: ANCHOR_CLASS,
      draggable: false,
      onPointerDown: stop,
      onPointerMove: noop,
      onPointerUp: noop,
      onPointerCancel: noop,
      onMouseDown: stop,
      onClick: stop,
      onDragStart: stopNativeDrag,
    };
  }

  return {
    className: ANCHOR_CLASS,
    draggable: false,
    onPointerDown: (e) => {
      e.stopPropagation();
      if (e.button != null && e.button !== 0) return; // primary button only
      try {
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
      } catch {
        /* jsdom / unsupported */
      }
      arbiter.down(e);
    },
    onPointerMove: (e) => arbiter.move(e),
    onPointerUp: (e) => {
      const el = e.currentTarget as HTMLElement;
      const wasTap = arbiter.up(e);
      if (!wasTap) blockNextClick(el); // real drag → do not activate; Grid already panned
      try {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      } catch {
        /* nop */
      }
    },
    onPointerCancel: (e) => {
      const el = e.currentTarget as HTMLElement;
      arbiter.cancel(e);
      blockNextClick(el);
      try {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      } catch {
        /* nop */
      }
    },
    onMouseDown: stop,
    // Native navigation proceeds for a genuine tap OR keyboard Enter; a drag was
    // already neutralised by blockNextClick. stopPropagation keeps Grid selection
    // from also firing.
    onClick: stop,
    onDragStart: stopNativeDrag,
  };
}
